import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBankAccounts,
  getActiveBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  uploadQrCode,
  getReceiptSequences,
  resetReceiptSequence,
} from '../data/bankAccountsRepository';
import type { BankAccountFormData } from '../types';

const BANK_ACCOUNTS_KEY = ['bank_accounts'];
const RECEIPT_SEQUENCES_KEY = ['receipt_sequences'];

export function useBankAccounts() {
  return useQuery({ queryKey: BANK_ACCOUNTS_KEY, queryFn: getBankAccounts });
}

export function useActiveBankAccounts() {
  return useQuery({ queryKey: [...BANK_ACCOUNTS_KEY, 'active'], queryFn: getActiveBankAccounts });
}

export function useReceiptSequences() {
  return useQuery({ queryKey: RECEIPT_SEQUENCES_KEY, queryFn: getReceiptSequences });
}

export function useBankAccountMutations() {
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: BANK_ACCOUNTS_KEY });
  };

  const create = useMutation({
    mutationFn: async (form: BankAccountFormData) => {
      const account = await createBankAccount({ ...form, qr_code_url: null });
      if (form.qr_code_file) {
        const url = await uploadQrCode(account.id, form.qr_code_file);
        await updateBankAccount(account.id, { qr_code_url: url });
      }
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, form }: { id: string; form: Partial<BankAccountFormData> }) => {
      const updates: Record<string, unknown> = { ...form };
      delete updates.qr_code_file;
      if (form.qr_code_file) {
        updates.qr_code_url = await uploadQrCode(id, form.qr_code_file);
      }
      await updateBankAccount(id, updates as never);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: deleteBankAccount,
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

export function useReceiptSequenceMutations() {
  const qc = useQueryClient();

  const reset = useMutation({
    mutationFn: ({ year, lastNumber }: { year: number; lastNumber: number }) =>
      resetReceiptSequence(year, lastNumber),
    onSuccess: () => qc.invalidateQueries({ queryKey: RECEIPT_SEQUENCES_KEY }),
  });

  return { reset };
}
